import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

const Legal = () => {
  return (
    <div className="min-h-screen pb-32 md:pb-8 bg-white">
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/prelanzamiento" onClick={() => window.scrollTo(0,0)}>
            <img src="/utentik-logo.png" alt="utentik" className="h-5" />
          </Link>
          <span className="text-gray-200">|</span>
          <Link to="/prelanzamiento" className="inline-flex items-center text-gray-400 hover:text-gray-500" onClick={() => window.scrollTo(0,0)}>
            <ChevronLeft className="w-4 h-4 mr-0.5" />
            Inicio
          </Link>
        </div>

        <h1 className="font-rubik text-2xl font-bold text-gray-500 mb-8">Términos de uso y privacidad</h1>

        {/* Terms of Service */}
        <section className="mb-10">
          <h2 className="font-rubik text-lg font-semibold text-gray-500 mb-4">Términos de uso</h2>
          
          <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
            <p>
              Al utilizar utentik, aceptas estos términos de uso. utentik es una plataforma que conecta 
              a creadores de actividades y talleres con usuarios interesados en participar.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Edad mínima</h3>
            <p>
              Para usar utentik, ya sea como usuario o como creador, debes ser mayor de 18 años. 
              Al registrarte, declaras que cumples con este requisito.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Responsabilidad del usuario</h3>
            <p>
              El usuario es responsable de investigar la veracidad y confiabilidad del creador de la actividad 
              antes de realizar cualquier pago o asistir a un evento. utentik actúa únicamente como intermediario 
              tecnológico para conectar creadores y asistentes, y <strong className="text-gray-500">no se hace responsable de fraudes, 
              incumplimientos, ni de la calidad de las actividades publicadas por los creadores</strong>. Recomendamos 
              verificar las reseñas de otros usuarios, el perfil del creador y cualquier información disponible 
              antes de comprometerse con una actividad.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Limitación de responsabilidad</h3>
            <p>
              utentik actúa únicamente como un espacio tecnológico de conexión entre creadores y usuarios. 
              No somos responsables de: fraudes o incumplimientos de cualquiera de las partes, la calidad o 
              veracidad de las actividades publicadas, disputas relacionadas con pagos realizados directamente 
              entre creador y usuario, ni interrupciones temporales del servicio por mantenimiento o causas 
              fuera de nuestro control. El uso de la plataforma es bajo tu propio riesgo, en la medida 
              permitida por la ley aplicable.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Seguridad en la comunicación</h3>
            <p>
              Por tu seguridad, <strong className="text-gray-500">recomendamos mantener toda la interacción entre usuario y creador 
              dentro de la plataforma utentik</strong>, utilizando el sistema de mensajería integrado. Si la comunicación 
              se lleva a cabo fuera de la plataforma (WhatsApp, correo externo, redes sociales, etc.), 
              utentik no podrá apoyar en aclaraciones, mediaciones ni resolución de conflictos. 
              La plataforma solo puede intervenir y mediar en situaciones donde exista registro de la 
              comunicación dentro del sistema.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Uso adecuado</h3>
            <p>
              Los usuarios se comprometen a utilizar la plataforma de manera responsable, sin publicar 
              contenido fraudulento, ofensivo o que viole derechos de terceros. utentik se reserva el 
              derecho de suspender cuentas que incumplan estos términos.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Contenido publicado por creadores</h3>
            <p>
              El creador conserva la propiedad de las fotografías, descripciones y demás contenido que publique 
              en utentik. Al publicarlo, el creador otorga a utentik una licencia no exclusiva para mostrar dicho 
              contenido dentro de la plataforma con el fin de promover su actividad. Esta licencia termina cuando 
              el creador elimina el contenido o su cuenta, salvo por copias ya generadas por el uso normal del 
              servicio (por ejemplo, reservas ya confirmadas).
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Pagos y transacciones</h3>
            <p>
              utentik facilita la conexión entre creadores y asistentes, pero los pagos se realizan 
              directamente entre las partes involucradas mediante los métodos configurados por el creador 
              (transferencia bancaria o link de pago externo). utentik no procesa ni retiene fondos.
            </p>
            <p>
              Dado que los pagos se realizan directamente entre el usuario y el creador fuera de los sistemas 
              de utentik, cualquier disputa relacionada con el pago (montos, reembolsos, comprobantes) es 
              responsabilidad exclusiva de ambas partes. utentik no media, garantiza ni resuelve disputas 
              de esta naturaleza.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Plazos de pago y confirmación</h3>
            <p>
              Al realizar una reserva en una actividad de pago, el usuario cuenta con un plazo de <strong className="text-gray-500">24 horas</strong> para 
              completar su pago y enviar el comprobante al creador a través de la plataforma. Si el pago no se realiza 
              dentro de este periodo, se activará un <strong className="text-gray-500">periodo de gracia de 24 horas adicionales</strong>. Si transcurridas 
              un total de 48 horas desde la reserva el pago no ha sido confirmado, la reserva será cancelada 
              automáticamente y el lugar quedará disponible para otros usuarios.
            </p>
            <p>
              El creador, por su parte, cuenta con <strong className="text-gray-500">24 horas</strong> desde que el usuario envía su comprobante para 
              revisar y confirmar el pago. Si pasadas las primeras 24 horas el creador no ha confirmado, se le 
              notificará y tendrá <strong className="text-gray-500">24 horas adicionales de gracia</strong> para hacerlo. Si al cumplirse un total de 
              48 horas el creador no confirma el pago, la reserva será cancelada automáticamente.
            </p>
            <p>
              <strong className="text-gray-500">En caso de cancelación por tiempo, si el creador ya recibió el pago del usuario, es responsabilidad 
              exclusiva del creador realizar el reembolso correspondiente.</strong> utentik no interviene ni se hace 
              responsable de reembolsos entre las partes.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Cancelaciones</h3>
            <p>
              Cada actividad tiene su propia política de cancelación definida por el creador. Es responsabilidad 
              del usuario revisar estas políticas antes de realizar una reserva o pago. Las cancelaciones voluntarias 
              realizadas por el usuario antes del plazo establecido por el creador no generan obligación de reembolso 
              por parte de utentik.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Ley aplicable</h3>
            <p>
              Estos términos se rigen por las leyes aplicables en los Estados Unidos Mexicanos. Cualquier 
              controversia relacionada con el uso de la plataforma se resolverá ante los tribunales competentes 
              de Zapopan, Jalisco, renunciando expresamente a cualquier otro fuero que pudiera corresponder 
              por razón de domicilio presente o futuro.
            </p>
          </div>
        </section>

        {/* Privacy Policy */}
        <section className="mb-10">
          <h2 className="font-rubik text-lg font-semibold text-gray-500 mb-4">Política de privacidad</h2>
          
          <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
            <h3 className="font-medium text-gray-500">Responsable del tratamiento de tus datos personales</h3>
            <p>
              utentik, con domicilio en Las Limas 1355-2, Girasoles Acueducto, Zapopan, Jalisco, México, es 
              responsable del uso y protección de tus datos personales conforme a lo previsto en la Ley Federal 
              de Protección de Datos Personales en Posesión de los Particulares. Para cualquier duda relacionada 
              con este aviso de privacidad, puedes contactarnos en{' '}
              <a href="mailto:soporte@utentik.com" className="text-primary hover:underline">soporte@utentik.com</a>.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Información que recopilamos</h3>
            <p>
              Recopilamos la información que proporcionas al crear tu cuenta (nombre, correo electrónico) 
              y la información generada por tu uso de la plataforma (reservas, reseñas, mensajes, favoritos).
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Uso de la información</h3>
            <p>
              Tus datos personales serán utilizados para las siguientes finalidades, necesarias para el servicio 
              que solicitas:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Crear y administrar tu cuenta</li>
              <li>Gestionar tus reservas, mensajes y reseñas dentro de la plataforma</li>
              <li>Dar soporte y atender solicitudes relacionadas con tu uso de utentik</li>
            </ul>
            <p>
              Adicionalmente, y de forma secundaria (es decir, no indispensable para el servicio), podríamos 
              usar tu información para:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Enviarte notificaciones promocionales o recomendaciones de actividades</li>
              <li>Realizar análisis estadístico para mejorar la plataforma</li>
            </ul>
            <p>
              Si no deseas que tus datos se usen para estas finalidades secundarias, puedes indicarlo escribiendo 
              a <a href="mailto:soporte@utentik.com" className="text-primary hover:underline">soporte@utentik.com</a> o 
              desde la sección de notificaciones de tu perfil. Tu negativa no afectará tu acceso a los servicios 
              esenciales de utentik.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Protección de datos</h3>
            <p>
              Implementamos medidas de seguridad para proteger tu información personal. Las contraseñas 
              se almacenan encriptadas y las comunicaciones se realizan a través de conexiones seguras (HTTPS).
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Transferencia de datos</h3>
            <p>
              Para operar la plataforma, compartimos ciertos datos con proveedores que actúan en nuestro nombre 
              (encargados), como Amplitude para fines de analítica de uso. Estos proveedores pueden almacenar 
              información en servidores fuera de México, y solo tratan tus datos conforme a nuestras instrucciones 
              y para las finalidades aquí descritas.
            </p>
            <p>
              No vendemos ni compartimos tu información personal con terceros para fines distintos a la operación 
              del servicio, salvo que la ley nos obligue a ello.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Tus derechos (ARCO)</h3>
            <p>
              Tienes derecho a Acceder, Rectificar y Cancelar tus datos personales, así como a Oponerte al uso 
              de los mismos para finalidades específicas (derechos ARCO). También puedes revocar el consentimiento 
              que en su caso hayas otorgado.
            </p>
            <p>
              Para ejercer cualquiera de estos derechos, envía tu solicitud a{' '}
              <a href="mailto:soporte@utentik.com" className="text-primary hover:underline">soporte@utentik.com</a> indicando: 
              (i) tu nombre y datos de contacto, (ii) el derecho que deseas ejercer, y (iii) cualquier documento 
              que ayude a acreditar tu identidad. Responderemos tu solicitud en un plazo máximo de 20 días hábiles, 
              conforme a lo establecido por la ley aplicable.
            </p>
            <p>
              Algunos ajustes básicos (acceso, modificación y eliminación de tu cuenta) también los puedes realizar 
              directamente desde la sección de perfil, sin necesidad de una solicitud formal.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Cambios a este aviso</h3>
            <p>
              Este aviso de privacidad puede sufrir modificaciones derivadas de nuevos requerimientos legales, 
              de nuestras propias necesidades por los servicios que ofrecemos, o por otras causas. Te notificaremos 
              cualquier cambio relevante a través de{' '}
              <a href="mailto:soporte@utentik.com" className="text-primary hover:underline">soporte@utentik.com</a> / aviso 
              dentro de la plataforma y, en cualquier caso, la versión vigente estará siempre disponible en esta misma página.
            </p>
          </div>
        </section>

        {/* Cookie Policy */}
        <section className="mb-10">
          <h2 className="font-rubik text-lg font-semibold text-gray-500 mb-4">Política de cookies y analítica</h2>
          
          <div className="space-y-4 text-sm text-gray-400 leading-relaxed">
            <h3 className="font-medium text-gray-500">¿Qué son las cookies?</h3>
            <p>
              Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas 
              nuestra plataforma. Nos ayudan a recordar tus preferencias y mejorar tu experiencia de uso.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Cookies que utilizamos</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-gray-500">Cookies esenciales:</strong> Necesarias para el funcionamiento 
                de la plataforma, como mantener tu sesión iniciada y recordar tus preferencias de idioma.
              </li>
              <li>
                <strong className="text-gray-500">Cookies de analítica:</strong> Utilizamos Amplitude para entender 
                cómo los usuarios interactúan con la plataforma. Esto nos permite mejorar la experiencia, identificar 
                problemas y optimizar el contenido. La información recopilada incluye páginas visitadas, acciones 
                realizadas (búsquedas, clics, registros) y datos técnicos del dispositivo.
              </li>
              <li>
                <strong className="text-gray-500">Grabación de sesión:</strong> Amplitude Session Replay puede 
                grabar de forma anónima las interacciones en pantalla para ayudarnos a entender cómo se usa la 
                plataforma y detectar errores. Estas grabaciones no capturan información sensible como contraseñas 
                ni datos de pago.
              </li>
            </ul>

            <h3 className="font-medium text-gray-500 mt-6">Tu consentimiento</h3>
            <p>
              Al visitar utentik por primera vez, te mostramos un aviso de cookies donde puedes aceptar o rechazar 
              las cookies de analítica. Si rechazas, Amplitude se desactiva y no se recopilan datos de uso. 
              Las cookies esenciales no se pueden desactivar ya que son necesarias para el funcionamiento de la plataforma.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Cómo cambiar tu preferencia</h3>
            <p>
              Puedes cambiar tu preferencia de cookies en cualquier momento limpiando los datos del sitio en 
              la configuración de tu navegador. Al volver a visitar utentik, se te mostrará nuevamente el aviso de cookies.
            </p>

            <h3 className="font-medium text-gray-500 mt-6">Terceros</h3>
            <p>
              La información de analítica es procesada por Amplitude (amplitude.com). Para más información sobre 
              cómo Amplitude maneja los datos, consulta su{' '}
              <a href="https://amplitude.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                política de privacidad
              </a>.
            </p>
          </div>
        </section>

        <div className="border-t border-gray-100 pt-6">
          <p className="text-xs text-gray-300 text-center">
            Última actualización: Julio 2026
          </p>
        </div>

        {/* Contact section */}
        <div className="mt-8 pt-8 border-t border-gray-100">
          <div className="text-center">
            <h3 className="font-rubik font-semibold text-lg mb-2">¿Tienes dudas?</h3>
            <p className="text-gray-400 mb-6">Contáctanos y te ayudaremos</p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                className="rounded-xl px-6 py-3 h-auto"
                onClick={() => window.location.href = 'mailto:soporte@utentik.com'}
              >
                <Mail className="w-5 h-5 mr-2" />
                soporte@utentik.com
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Legal;
